import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, RefreshCw, CheckCircle2, XCircle, Eye, Layers, Wand2, Download,
  ArrowUp, ArrowDown, X, ChevronRight, ChevronDown, Landmark, ShieldCheck,
  CheckCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import ReviewedBadge from '../components/ReviewedBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import api from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useEnvInfo } from '../hooks/useEnvInfo';
import { formatCurrency, formatDateOnly, shortId } from '../lib/utils';

export default function DepositosPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const isAllowed = ['admin', 'accountant'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const { demoToolsEnabled } = useEnvInfo();
  const [reconciling, setReconciling] = useState(false);
  const [preparingDemo, setPreparingDemo] = useState(false);
  const [demoPhase, setDemoPhase] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const stopDemoPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPreparingDemo(false);
    setDemoPhase(null);
  };

  const handlePrepareDemo = async () => {
    setPreparingDemo(true);
    setDemoPhase('backfill');
    try {
      const res = await api.post('/deposits/test/prepare-demo');
      if (res.data?.already_running) {
        toast.info(t('deposits.demo_already_running'));
      }
      pollRef.current = setInterval(async () => {
        try {
          const st = (await api.get('/deposits/test/prepare-demo/status')).data || {};
          setDemoPhase(st.phase);
          if (st.phase === 'done') {
            stopDemoPolling();
            toast.success(
              `${st.orders_backfilled ?? 0} órdenes · ${st.estimates_created ?? 0} estimates · ` +
              `${st.estimates_converted ?? 0} facturas · ${st.balanced_count ?? 0}/${st.previews_refreshed ?? 0} cuadran`
            );
            qc.invalidateQueries({ queryKey: ['deposits'] });
          } else if (st.phase === 'error') {
            stopDemoPolling();
            toast.error(st.error || t('deposits.demo_error'));
            qc.invalidateQueries({ queryKey: ['deposits'] });
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      stopDemoPolling();
      toast.error(e?.response?.data?.detail || t('deposits.demo_error'));
    }
  };

  const demoPhaseLabel = () => {
    if (demoPhase === 'backfill') return t('deposits.demo_phase_backfill');
    if (demoPhase === 'invoicing') return t('deposits.demo_phase_invoicing');
    if (demoPhase === 'refreshing') return t('deposits.demo_phase_refreshing');
    return t('deposits.preparing_demo');
  };
  const [activeTab, setActiveTab] = useState('pending');
  const [selected, setSelected] = useState(() => new Set());
  const [includeAll, setIncludeAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDate, setBulkDate] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // ── Bank-payout grouped view ─────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(
    () => (searchParams.get('payment') ? 'payouts' : 'statements'));
  const [expanded, setExpanded] = useState(() => new Set());
  const [groupDetails, setGroupDetails] = useState({}); // group_key -> fresh GET /payouts/{id} group
  const [groupCommit, setGroupCommit] = useState(null); // {group, date}
  const [groupJob, setGroupJob] = useState(null); // progreso del commit en curso
  const [blockedModal, setBlockedModal] = useState(null); // {key, payoutTotal, blocked, blockedTotal}
  const [groupMark, setGroupMark] = useState(null);     // {group, note}
  const [groupBusy, setGroupBusy] = useState(false);
  const [aggBusy, setAggBusy] = useState(null);   // statement_id en curso
  // Fix 30 (2026-09-04): ajuste a nivel payout bancario.
  const [gAdj, setGAdj] = useState(null);   // {group, amount, account, note}
  const [gAdjBusy, setGAdjBusy] = useState(false);
  const adjAccountsQ = useQuery({
    queryKey: ['deposits', 'adjustment-accounts'],
    queryFn: async () => (await api.get('/deposits/adjustment-accounts')).data,
  });
  const submitGroupAdjustment = async () => {
    if (!gAdj) return;
    setGAdjBusy(true);
    try {
      const res = await api.post(
        `/deposits/payouts/${encodeURIComponent(gAdj.group.group_key)}/group-adjustments`,
        { amount: gAdj.amount, account_name: gAdj.account, note: gAdj.note || null });
      setGroupDetails((prev) => ({ ...prev, [gAdj.group.group_key]: res.data?.group }));
      qc.invalidateQueries({ queryKey: ['deposits'] });
      toast.success(t('deposits.group_adj_added'));
      setGAdj(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'error');
    } finally {
      setGAdjBusy(false);
    }
  };
  const removeGroupAdjustment = async (groupKey, adjId) => {
    try {
      const res = await api.delete(
        `/deposits/payouts/${encodeURIComponent(groupKey)}/group-adjustments/${adjId}`);
      setGroupDetails((prev) => ({ ...prev, [groupKey]: res.data?.group }));
      qc.invalidateQueries({ queryKey: ['deposits'] });
      toast.success(t('deposits.group_adj_removed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'error');
    }
  };
  const [backfill, setBackfill] = useState(null);
  const [excludeBusy, setExcludeBusy] = useState(false);
  const backfillRef = useRef(null);

  useEffect(() => () => {
    if (backfillRef.current) clearInterval(backfillRef.current);
  }, []);

  // Deep link: /depositos?payment=<payment_id> opens the grouped view focused
  // on that bank payout (used by the ⌘K palette and the statement detail).
  useEffect(() => {
    const p = searchParams.get('payment');
    if (p) {
      setViewMode('payouts');
      setSearchText(p);
      setExpanded(new Set([p]));
    }
  }, [searchParams]);

  // Resume the progress indicator if a backfill was already running.
  useEffect(() => {
    if (!isAllowed) return;
    (async () => {
      try {
        const st = (await api.get('/deposits/reconcile-pending/status')).data || {};
        if (st.phase === 'running') {
          setBackfill(st);
          setReconciling(true);
          startBackfillPolling();
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const handleReconcilePending = async () => {
    setReconciling(true);
    try {
      const res = await api.post('/deposits/reconcile-pending');
      if (res.data?.already_running) {
        toast.info(t('deposits.backfill_already_running'));
      } else {
        toast.success(t('deposits.backfill_started'));
        setBackfill({ phase: 'pulling' });
      }
      startBackfillPolling();
    } catch (e) {
      setReconciling(false);
      toast.error(e?.response?.data?.detail || 'Error al reconciliar pendientes');
    }
  };

  const startBackfillPolling = () => {
    if (backfillRef.current) clearInterval(backfillRef.current);
    backfillRef.current = setInterval(async () => {
      try {
        const st = (await api.get('/deposits/reconcile-pending/status')).data || {};
        setBackfill(st);
        if (st.phase === 'done' || st.phase === 'error' || st.phase === 'idle') {
          clearInterval(backfillRef.current);
          backfillRef.current = null;
          setReconciling(false);
          if (st.phase === 'error') {
            toast.error(st.error || t('deposits.backfill_error'));
          } else if (st.phase === 'done') {
            toast.success(
              t('deposits.backfill_done')
                .replace('{{reconciled}}', st.reconciled ?? 0)
                .replace('{{balanced}}', st.balanced ?? 0)
                .replace('{{unbalanced}}', st.unbalanced ?? 0)
            );
            const empty = st.pull?.empty_subranges || [];
            if (empty.length > 0) {
              toast.warning(
                `${t('deposits.backfill_empty_ranges')} ${empty.map((e) => `${e.from}→${e.to}`).join(', ')}`,
                { duration: 15000 }
              );
            }
            if (st.payments) {
              const n = st.payments.pulled ?? 0;
              if (n > 0) {
                toast.success(t('deposits.backfill_payments').replace('{{n}}', n));
              } else {
                toast.warning(t('deposits.backfill_payments_empty'), { duration: 15000 });
              }
            }
          }
          qc.invalidateQueries({ queryKey: ['deposits'] });
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const q = useQuery({
    queryKey: ['deposits', 'list'],
    queryFn: async () => (await api.get('/deposits')).data,
    enabled: isAllowed,
  });

  const groupsQ = useQuery({
    queryKey: ['deposits', 'payouts'],
    queryFn: async () => (await api.get('/deposits/payouts')).data,
    enabled: isAllowed && viewMode === 'payouts',
  });

  // Statements que EXISTEN en db.settlements pero todavía no tienen depósito
  // (invisibles en la vista de payouts) — nunca más un "no existe".
  const unprocessedQ = useQuery({
    queryKey: ['deposits', 'statement-search', searchText.trim()],
    queryFn: async () => (await api.get('/deposits/statements/search', {
      params: { q: searchText.trim() } })).data,
    enabled: isAllowed && searchText.trim().length >= 3,
  });

  // Búsqueda INVERSA: nº de factura (DocNumber de QB) o nº de orden de TikTok →
  // en QUÉ payout está incluido.
  const refQ = useQuery({
    queryKey: ['deposits', 'find-by', searchText.trim()],
    queryFn: async () => (await api.get('/deposits/find-by', {
      params: { q: searchText.trim() } })).data,
    enabled: isAllowed && searchText.trim().length >= 3,
  });

  const generateAggregate = async (statementId) => {
    setAggBusy(statementId);
    try {
      await api.post(
        `/deposits/statements/${encodeURIComponent(statementId)}/generate-aggregate`);
      toast.success(t('deposits.generate_aggregate_success')
        .replace('{{id}}', statementId));
      qc.invalidateQueries({ queryKey: ['deposits'] });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && (detail.message || detail))
        || t('deposits.generate_aggregate_error'), { duration: 9000 });
    } finally {
      setAggBusy(null);
    }
  };

  if (!isAllowed) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground text-sm" data-testid="deposits-access-denied">
        {t('deposits.access_denied')}
      </div>
    );
  }

  const all = q.data?.deposits || [];

  const inDateRange = (d) => {
    if (!dateFrom && !dateTo) return true;
    const pd = d.payout_date || '';
    if (dateFrom && pd < dateFrom) return false;
    if (dateTo && pd > dateTo) return false;
    return true;
  };
  const trimmedSearch = searchText.trim();
  const matchesSearch = (d) => !trimmedSearch
    || String(d.payout_id || '').includes(trimmedSearch)
    || String(d.payment_id || '').includes(trimmedSearch);
  const sortRows = (rows) => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let av, bv;
      if (sortBy === 'net') {
        av = typeof a.net === 'number' ? a.net : parseFloat(a.net) || 0;
        bv = typeof b.net === 'number' ? b.net : parseFloat(b.net) || 0;
      } else {
        av = a.payout_date || '';
        bv = b.payout_date || '';
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  };

  const filteredAll = all.filter((d) => inDateRange(d) && matchesSearch(d));
  const pending = sortRows(filteredAll.filter(d => d.status === 'pending_review'));
  const committed = sortRows(filteredAll.filter(d => d.status === 'committed'));
  const externalReconciled = sortRows(filteredAll.filter(d => d.status === 'reconciled_externally'));
  const hasDateFilter = !!(dateFrom || dateTo);
  const hasTextFilter = !!trimmedSearch;
  const hasActiveFilter = hasDateFilter || hasTextFilter;

  const scopeRows = includeAll ? filteredAll
    : activeTab === 'pending' ? pending
    : activeTab === 'committed' ? committed
    : externalReconciled;
  const exportCount = selected.size > 0 ? selected.size : scopeRows.length;

  // Bulk "mark as reconciled externally" — counts over the FULL loaded list
  // (ignores UI filters) so it matches exactly what the backend will select.
  const allPending = all.filter(d => d.status === 'pending_review');
  const todayStr = new Date().toISOString().slice(0, 10);
  const bulkCount = bulkDate
    ? allPending.filter(d => d.payout_date && d.payout_date < bulkDate).length
    : 0;
  const bulkDateValid = !!bulkDate && bulkDate <= todayStr;
  const selectedPendingIds = [...selected].filter(
    id => allPending.some(p => p.payout_id === id));

  const openBulkModal = () => {
    const monthPrefix = todayStr.slice(0, 7);
    const oldestThisMonth = allPending
      .map(p => p.payout_date)
      .filter(d => d && d.startsWith(monthPrefix))
      .sort()[0];
    setBulkDate(oldestThisMonth || todayStr);
    setBulkNote(t('deposits.bulk_mark_note_default'));
    setBulkOpen(true);
  };

  const runBulkMark = async (payload) => {
    setBulkSubmitting(true);
    try {
      const res = await api.post('/deposits/bulk-mark-reconciled', payload);
      const d = res.data || {};
      toast.success(`${d.marked ?? 0} ${t('deposits.bulk_mark_success_suffix')}`);
      setBulkOpen(false);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['deposits'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('deposits.bulk_mark_error'));
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkMarkByDate = () => runBulkMark({
    before_date: bulkDate,
    note: bulkNote || undefined,
  });
  const handleBulkMarkSelected = () => runBulkMark({
    payout_ids: selectedPendingIds,
    note: t('deposits.bulk_mark_note_default'),
  });

  const toggleRow = (payoutId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(payoutId)) next.delete(payoutId); else next.add(payoutId);
      return next;
    });
  };
  const toggleAllRows = (rows, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      rows.forEach(r => { if (checked) next.add(r.payout_id); else next.delete(r.payout_id); });
      return next;
    });
  };

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const params = { format: fmt };
      if (selected.size > 0) {
        params.payout_ids = [...selected].join(',');
        params.scope = 'all';
      } else if (hasActiveFilter || activeTab === 'external') {
        // Respect the active date/text filter — download exactly what's visible.
        // The "external" tab has no dedicated backend scope, so it also goes
        // through payout_ids (same pattern as an active filter).
        params.payout_ids = scopeRows.map(r => r.payout_id).join(',');
        params.scope = 'all';
      } else {
        params.scope = includeAll ? 'all' : (activeTab === 'pending' ? 'pending' : 'approved');
      }
      const res = await api.get('/deposits/export', { params, responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `lovi-depositos.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${exportCount} ${t('deposits.export_done')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('deposits.export_error'));
    } finally {
      setExporting(false);
    }
  };

  // ── Bank-payout groups (view "Payouts del banco") ──────────────────────
  const allGroups = groupsQ.data?.groups || [];
  const groupInDateRange = (g) => {
    if (!dateFrom && !dateTo) return true;
    const d = g.last_statement_date || '';
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };
  const filteredGroups = allGroups
    .map((g) => (groupDetails[g.group_key] ? { ...g, ...groupDetails[g.group_key] } : g))
    .filter((g) => {
      if (!groupInDateRange(g)) return false;
      if (!trimmedSearch) return true;
      // La contadora no tiene el id del descriptor bancario → puede buscar por el
      // MONTO del extracto (ej. "9554.51" o "9,554.51"), con tolerancia de $0.01.
      const asAmount = Number(trimmedSearch.replace(/[$\s,]/g, ''));
      if (Number.isFinite(asAmount) && asAmount > 0
          && Math.abs(Number(g.total) - asAmount) <= 0.011) return true;
      return String(g.payment_id || '').includes(trimmedSearch)
        || String(g.bank_payment_id || '').includes(trimmedSearch)
        || (g.missing_statement_ids || []).some(
          (sid) => String(sid).includes(trimmedSearch))
        || (g.statements || []).some(
          (s) => String(s.payout_id || '').includes(trimmedSearch));
    });

  // Statements que existen en el sistema pero NO tienen depósito generado.
  const unprocessedStatements = (unprocessedQ.data?.statements || [])
    .filter((s) => !s.has_preview);

  // Matches de factura/orden DENTRO de un payout — se muestran SIEMPRE (además
  // de los matches locales de la lista, no en reemplazo): un número de factura
  // puede coincidir por casualidad con parte de un payment id.
  const referenceMatches = (() => {
    if (!trimmedSearch) return [];
    const rows = refQ.data?.matches || [];
    const rank = (r) => {
      if (r.match_kind === 'invoice'
        && String(r.doc_number || '') === trimmedSearch) return 0;
      if ((r.order_ids || []).some((o) => String(o) === trimmedSearch)) return 1;
      return 2;
    };
    return [...rows].sort((a, b) => rank(a) - rank(b));
  })();

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // Al abrir el detalle: si el payout está committed, el backend
        // verifica (cacheado) si el Bank Deposit sigue existiendo en QBO.
        api.get(`/deposits/payouts/${encodeURIComponent(key)}`)
          .then((res) => setGroupDetails((prev2) => ({ ...prev2, [key]: res.data?.group })))
          .catch(() => {});
      }
      return next;
    });
  };

  const groupError = (e, fallbackKey) => {
    const detail = e?.response?.data?.detail;
    if (detail && typeof detail === 'object' && detail.reason === 'group_blocked') {
      const list = (detail.blockers || [])
        .map((b) => (b.payout_id
          ? `${b.payout_id}: ${t(`deposits.blocker_${b.reason}`)}`
          : t(`deposits.blocker_${b.reason}`)))
        .join(' · ');
      toast.error(`${t('deposits.group_blocked_title')} — ${list}`, { duration: 9000 });
      return;
    }
    toast.error((detail && (detail.message || detail)) || t(fallbackKey));
  };

  const pollGroupJob = async (key, payoutTotal) => {
    for (let i = 0; i < 900; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 2000));
      let job = null;
      try {
        // eslint-disable-next-line no-await-in-loop
        const st = await api.get(
          `/deposits/payouts/${encodeURIComponent(key)}/commit-status`);
        job = st.data || {};
      } catch (e) { continue; }
      setGroupJob(job);
      if (job.status === 'committed') {
        toast.success(`${t('deposits.group_commit_success')} ${job.deposit_id || ''}`);
        if (job.payments_recreated > 0) {
          toast.info(
            `${job.payments_recreated} ${t('deposits.payments_recreated_toast')}`,
            { duration: 12000, id: 'deposits-payments-recreated-toast' },
          );
        }
        setGroupCommit(null);
        setGroupJob(null);
        setGroupDetails((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        qc.invalidateQueries({ queryKey: ['deposits'] });
        return;
      }
      if (job.status === 'error') {
        if (job.reason === 'blocked_payments' && Array.isArray(job.blocked_payments)
          && job.blocked_payments.length) {
          setBlockedModal({
            key,
            payoutTotal: payoutTotal ?? groupCommit?.group?.total,
            blocked: job.blocked_payments,
            blockedTotal: job.blocked_total,
          });
          setGroupCommit(null);
          setGroupJob(null);
          return;
        }
        toast.error(job.error || t('deposits.group_commit_error'), { duration: 14000 });
        qc.invalidateQueries({ queryKey: ['deposits'] });
        return;
      }
    }
  };

  const runGroupCommit = async () => {
    if (!groupCommit || groupBusy) return;
    const key = groupCommit.group.group_key;
    const payoutTotal = groupCommit.group.total;
    setGroupBusy(true);
    setGroupJob({ status: 'running', stage: 'starting', payments_created: 0, payments_total: 0 });
    try {
      const res = await api.post(
        `/deposits/payouts/${encodeURIComponent(key)}/commit`,
        { txn_date: groupCommit.date || undefined });
      const d = res.data || {};
      if (d.already_committed) {
        toast.success(`${t('deposits.group_commit_success')} ${d.deposit_id || ''}`);
        setGroupCommit(null);
        setGroupJob(null);
        qc.invalidateQueries({ queryKey: ['deposits'] });
      } else {
        await pollGroupJob(key, payoutTotal);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'string') {
        toast.error(detail);
        await pollGroupJob(key, payoutTotal);
      } else {
        setGroupJob(null);
        groupError(e, 'deposits.group_commit_error');
      }
    } finally {
      setGroupBusy(false);
    }
  };

  const excludeAndCommit = async () => {
    if (!blockedModal || excludeBusy) return;
    const { key, blocked } = blockedModal;
    setExcludeBusy(true);
    setGroupJob({ status: 'running', stage: 'starting', payments_created: 0, payments_total: 0 });
    try {
      await api.post(
        `/deposits/payouts/${encodeURIComponent(key)}/commit-exclude-blocked`,
        { payment_ids: blocked.map((b) => b.payment_id) });
      setBlockedModal(null);
      await pollGroupJob(key);
    } catch (e) {
      setGroupJob(null);
      groupError(e, 'deposits.blocked_payments_exclude_error');
    } finally {
      setExcludeBusy(false);
    }
  };

  // Estado intermedio "Revisado ✓" — NO cambia el status ni escribe en QBO.
  const toggleReview = async (payoutId) => {
    try {
      const res = await api.post(
        `/deposits/${encodeURIComponent(payoutId)}/toggle-review`);
      toast.success(res.data?.reviewed
        ? t('deposits.review_marked')
        : t('deposits.review_unmarked'));
      qc.invalidateQueries({ queryKey: ['deposits'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('deposits.review_error'));
    }
  };

  const runGroupMark = async () => {    if (!groupMark) return;
    setGroupBusy(true);
    try {
      const res = await api.post(
        `/deposits/payouts/${encodeURIComponent(groupMark.group.group_key)}/mark-reconciled`,
        { note: groupMark.note || undefined });
      toast.success(`${res.data?.marked ?? 0} ${t('deposits.group_mark_success')}`);
      setGroupMark(null);
      qc.invalidateQueries({ queryKey: ['deposits'] });
    } catch (e) {
      groupError(e, 'deposits.group_mark_error');
    } finally {
      setGroupBusy(false);
    }
  };

  return (
    <div data-testid="depositos-page">
      <PageHeader
        title={t('deposits.title')}
        subtitle={t('deposits.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={exporting || exportCount === 0}
                  data-testid="deposits-download-btn"
                >
                  {exporting
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <Download className="h-4 w-4 mr-2" />}
                  {t('deposits.btn_download')}
                  <span className="ml-2 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold px-1.5 py-0.5 tabular-nums" data-testid="deposits-export-count">
                    {exportCount}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="deposits-export-csv">
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="deposits-export-xlsx">
                  Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={includeAll}
                  onCheckedChange={setIncludeAll}
                  onSelect={(e) => e.preventDefault()}
                  data-testid="deposits-export-include-all"
                >
                  {t('deposits.export_include_all')}
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {isAdmin && demoToolsEnabled && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handlePrepareDemo}
                disabled={preparingDemo}
                data-testid="deposits-prepare-demo-btn"
              >
                {preparingDemo
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Wand2 className="h-4 w-4 mr-2" />}
                {preparingDemo ? demoPhaseLabel() : t('deposits.btn_prepare_demo')}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="default"
                size="sm"
                onClick={handleReconcilePending}
                disabled={reconciling}
                data-testid="deposits-reconcile-pending-btn"
              >
                {reconciling
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Layers className="h-4 w-4 mr-2" />}
                {reconciling
                  ? (backfill?.phase === 'pulling'
                    ? t('deposits.backfill_pulling')
                    : (backfill?.total
                      ? t('deposits.backfill_running')
                        .replace('{{done}}', backfill.processed ?? 0)
                        .replace('{{total}}', backfill.total)
                      : t('deposits.reconciling')))
                  : t('deposits.btn_reconcile_pending')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: ['deposits'] })}
              disabled={q.isFetching}
              data-testid="deposits-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${q.isFetching ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </Button>
          </div>
        }
      />

      {q.isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!q.isLoading && (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="deposits-date-filter">
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t('deposits.search_payout_placeholder')}
                className="h-8 w-56 text-xs"
                data-testid="deposits-search-payout-input"
              />
              {hasTextFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => setSearchText('')}
                  data-testid="deposits-search-payout-clear-btn"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="deposits-date-from" className="text-xs text-muted-foreground whitespace-nowrap">
                {t('deposits.filter_from')}
              </Label>
              <Input
                id="deposits-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 w-40 text-xs"
                data-testid="deposits-date-from-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="deposits-date-to" className="text-xs text-muted-foreground whitespace-nowrap">
                {t('deposits.filter_to')}
              </Label>
              <Input
                id="deposits-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 w-40 text-xs"
                data-testid="deposits-date-to-input"
              />
            </div>
            {hasDateFilter && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                data-testid="deposits-date-clear-btn"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t('deposits.filter_clear')}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 mb-4" data-testid="deposits-view-toggle">
            <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
              <button
                type="button"
                onClick={() => setViewMode('payouts')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${viewMode === 'payouts' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="deposits-view-payouts-btn"
              >
                <Landmark className="h-3.5 w-3.5" />
                {t('deposits.view_payouts')}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('statements')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'statements' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="deposits-view-statements-btn"
              >
                {t('deposits.view_statements')}
              </button>
            </div>
            {viewMode === 'payouts' && (
              <span className="text-[11px] text-muted-foreground">
                {t('deposits.view_payouts_hint')}
              </span>
            )}
          </div>

          {unprocessedStatements.length > 0 && (
            <UnprocessedStatements
              rows={unprocessedStatements}
              onGenerate={generateAggregate}
              busyId={aggBusy}
              navigate={navigate}
              locale={locale}
              t={t}
            />
          )}

          {referenceMatches.length > 0 && (
            <DepositReferenceMatches
              rows={referenceMatches}
              navigate={navigate}
              locale={locale}
              t={t}
            />
          )}

          {viewMode === 'payouts' && (
            <PayoutGroupsTable
              groups={filteredGroups}
              loading={groupsQ.isLoading}
              expanded={expanded}
              toggleGroup={toggleGroup}
              onCommit={(g) => setGroupCommit({ group: g, date: g.default_txn_date || todayStr })}
              onMark={(g) => setGroupMark({ group: g, note: t('deposits.bulk_mark_note_default') })}
              onToggleReview={toggleReview}
              onGenerateAggregate={generateAggregate}
              aggBusy={aggBusy}
              navigate={navigate}
              locale={locale}
              t={t}
              setGAdj={setGAdj}
              removeGroupAdjustment={removeGroupAdjustment}
            />
          )}

          {viewMode === 'statements' && (
          <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="deposits-tabs">
            <TabsList>
              <TabsTrigger value="pending" data-testid="deposits-tab-pending">
                {t('deposits.tab_pending')}
                {pending.length > 0 && (
                  <span className="ml-2 rounded-full bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border border-[hsl(var(--warning-border))] text-[10px] font-semibold px-1.5 py-0.5 tabular-nums">
                    {pending.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="committed" data-testid="deposits-tab-committed">
                {t('deposits.tab_committed')}
                {committed.length > 0 && (
                  <span className="ml-2 rounded-full bg-[hsl(var(--success-bg))] text-[hsl(var(--success-fg))] border border-[hsl(var(--success-border))] text-[10px] font-semibold px-1.5 py-0.5 tabular-nums">
                    {committed.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="external" data-testid="deposits-tab-external">
                {t('deposits.tab_external')}
                {externalReconciled.length > 0 && (
                  <span className="ml-2 rounded-full bg-[hsl(var(--info-bg))] text-[hsl(var(--info-fg))] border border-[hsl(var(--info-border))] text-[10px] font-semibold px-1.5 py-0.5 tabular-nums">
                    {externalReconciled.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-4">
              <div className="flex items-center justify-end gap-2 mb-2">
                {selectedPendingIds.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkMarkSelected}
                    disabled={bulkSubmitting}
                    data-testid="deposits-bulk-mark-selected-btn"
                  >
                    {bulkSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t('deposits.bulk_mark_selected_btn')} ({selectedPendingIds.length})
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={openBulkModal}
                  data-testid="deposits-bulk-mark-open-btn"
                >
                  {t('deposits.bulk_mark_btn')}
                </Button>
              </div>
              <PayoutTable rows={pending} locale={locale} t={t} navigate={navigate} emptyKey="deposits.empty_pending"
                selected={selected} toggleRow={toggleRow} toggleAllRows={toggleAllRows}
                onToggleReview={toggleReview}
                sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </TabsContent>

            <TabsContent value="committed" className="mt-4">
              <PayoutTable rows={committed} locale={locale} t={t} navigate={navigate} emptyKey="deposits.empty_committed"
                selected={selected} toggleRow={toggleRow} toggleAllRows={toggleAllRows}
                sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </TabsContent>

            <TabsContent value="external" className="mt-4">
              <PayoutTable rows={externalReconciled} locale={locale} t={t} navigate={navigate} emptyKey="deposits.empty_external"
                selected={selected} toggleRow={toggleRow} toggleAllRows={toggleAllRows}
                sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </TabsContent>
          </Tabs>
          )}
        </>
      )}

      {/* Fix 30 (2026-09-04): ajuste a nivel payout bancario. */}
      <Dialog open={!!gAdj} onOpenChange={(o) => !o && setGAdj(null)}>
        <DialogContent data-testid="deposits-group-adjustment-dialog">
          <DialogHeader>
            <DialogTitle>{t('deposits.group_adj_title')}</DialogTitle>
            <DialogDescription>{t('deposits.group_adj_desc')}</DialogDescription>
          </DialogHeader>
          {gAdj && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground space-y-1">
                <div>{t('deposits.group_adj_bank')}: <span className="tabular-nums font-semibold">{formatCurrency(gAdj.group.total, locale)}</span></div>
                <div>{t('deposits.group_adj_statements')}: <span className="tabular-nums font-semibold">{formatCurrency(gAdj.group.statements_sum, locale)}</span></div>
                <div>{t('deposits.group_adj_missing')}: <span className="tabular-nums font-semibold">{formatCurrency(gAdj.group.missing_to_bank, locale)}</span></div>
              </div>
              <div>
                <Label className="text-xs">{t('deposits.group_adj_amount')}</Label>
                <Input value={gAdj.amount}
                  onChange={(e) => setGAdj({ ...gAdj, amount: e.target.value })}
                  data-testid="group-adj-amount" />
              </div>
              <div>
                <Label className="text-xs">{t('deposits.group_adj_account')}</Label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={gAdj.account}
                  onChange={(e) => setGAdj({ ...gAdj, account: e.target.value })}
                  data-testid="group-adj-account">
                  <option value="">{t('deposits.group_adj_account')}</option>
                  {(adjAccountsQ.data?.accounts || [])
                    .filter((a) => !a.missing)
                    .map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">{t('deposits.group_adj_note')}</Label>
                <Input value={gAdj.note}
                  onChange={(e) => setGAdj({ ...gAdj, note: e.target.value })}
                  data-testid="group-adj-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGAdj(null)}>{t('common.cancel')}</Button>
            <Button onClick={submitGroupAdjustment}
              disabled={gAdjBusy || !gAdj?.account || !gAdj?.amount}
              data-testid="group-adj-submit">
              {gAdjBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('deposits.group_adj_add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="deposits-bulk-mark-dialog">
          <DialogHeader>
            <DialogTitle>{t('deposits.bulk_mark_title')}</DialogTitle>
            <DialogDescription>{t('deposits.bulk_mark_dialog_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="bulk-mark-date" className="text-xs">
                {t('deposits.bulk_mark_before_label')}
              </Label>
              <Input
                id="bulk-mark-date"
                type="date"
                value={bulkDate}
                max={todayStr}
                onChange={(e) => setBulkDate(e.target.value)}
                className="h-8 w-44 text-xs"
                data-testid="deposits-bulk-mark-date-input"
              />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="deposits-bulk-mark-count">
              {bulkCount > 0
                ? `${bulkCount} ${t('deposits.bulk_mark_count_suffix')}`
                : t('deposits.bulk_mark_none')}
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="bulk-mark-note" className="text-xs">
                {t('deposits.mark_reconciled_note')}
              </Label>
              <Input
                id="bulk-mark-note"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                className="h-8 text-xs"
                data-testid="deposits-bulk-mark-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkOpen(false)}
              data-testid="deposits-bulk-mark-cancel-btn"
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={handleBulkMarkByDate}
              disabled={bulkSubmitting || !bulkDateValid || bulkCount === 0}
              data-testid="deposits-bulk-mark-confirm-btn"
            >
              {bulkSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {`${t('deposits.bulk_mark_confirm_prefix')} ${bulkCount} ${t('deposits.bulk_mark_confirm_suffix')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Aprobar payout bancario completo ───────────────────────────── */}
      <Dialog open={!!groupCommit}
        onOpenChange={(o) => { if (!o && !groupBusy && groupJob?.status !== 'running') { setGroupCommit(null); setGroupJob(null); } }}>
        <DialogContent data-testid="deposits-group-commit-dialog">
          <DialogHeader>
            <DialogTitle>{t('deposits.group_commit_title')}</DialogTitle>
            <DialogDescription>{t('deposits.group_commit_desc')}</DialogDescription>
          </DialogHeader>
          {groupCommit && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border p-3 space-y-1">
                <p className="font-mono text-xs" data-testid="deposits-group-commit-payment-id">
                  {groupCommit.group.payment_id || groupCommit.group.group_key}
                </p>
                <p className="text-sm font-semibold" data-testid="deposits-group-commit-total">
                  {formatCurrency(groupCommit.group.total, locale)}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    · {groupCommit.group.statement_count} {t('deposits.view_statements')}
                  </span>
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="group-commit-date" className="text-xs">
                  {t('deposits.group_commit_date_label')}
                </Label>
                <Input
                  id="group-commit-date"
                  type="date"
                  value={groupCommit.date || ''}
                  onChange={(e) => setGroupCommit({ ...groupCommit, date: e.target.value })}
                  className="h-8 w-44 text-xs"
                  data-testid="deposits-group-commit-date-input"
                />
                <p className="text-[11px] text-muted-foreground">
                  {groupCommit.group.paid_date_known
                    ? t('deposits.group_commit_date_hint_known')
                    : t('deposits.group_commit_date_hint_unknown')}
                </p>
              </div>
              {groupJob && (
                <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-1"
                  data-testid="deposits-group-commit-progress">
                  <p className="text-xs font-semibold flex items-center gap-2">
                    {groupJob.status === 'running' && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    )}
                    {t('deposits.group_commit_progress')}: {groupJob.stage || '—'}
                  </p>
                  <p className="text-[11px] text-muted-foreground"
                    data-testid="deposits-group-commit-progress-count">
                    {(groupJob.payments_created ?? 0)} / {(groupJob.payments_total ?? 0)} payments
                  </p>
                  {groupJob.error && (
                    <p className="text-[11px] text-destructive"
                      data-testid="deposits-group-commit-progress-error">
                      {groupJob.error}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setGroupCommit(null)}
              disabled={groupBusy}
              data-testid="deposits-group-commit-cancel-btn">
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={runGroupCommit}
              disabled={groupBusy || !groupCommit?.date
                || groupJob?.status === 'running'}
              data-testid="deposits-group-commit-confirm-btn">
              {groupBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('deposits.group_commit_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Pagos ya incluidos en OTRO depósito de QuickBooks ──────────── */}
      <Dialog open={!!blockedModal}
        onOpenChange={(o) => { if (!o && !excludeBusy) setBlockedModal(null); }}>
        <DialogContent data-testid="deposits-blocked-payments-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('deposits.blocked_payments_title')}</DialogTitle>
            <DialogDescription>{t('deposits.blocked_payments_desc')}</DialogDescription>
          </DialogHeader>
          {blockedModal && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border overflow-hidden max-h-64 overflow-y-auto">
                <Table data-testid="deposits-blocked-payments-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('deposits.blocked_payments_col_invoice')}</TableHead>
                      <TableHead>{t('deposits.blocked_payments_col_customer')}</TableHead>
                      <TableHead className="text-right">{t('deposits.blocked_payments_col_amount')}</TableHead>
                      <TableHead>{t('deposits.blocked_payments_col_deposit')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blockedModal.blocked.map((b) => (
                      <TableRow key={b.payment_id}
                        data-testid={`deposits-blocked-payment-row-${b.payment_id}`}>
                        <TableCell className="font-mono text-xs">
                          {b.invoice_doc_number || '—'}
                        </TableCell>
                        <TableCell className="text-xs">{b.customer_name || '—'}</TableCell>
                        <TableCell className="text-right text-xs">
                          {formatCurrency(b.amount, locale)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {b.deposit_id
                            ? `${b.deposit_id}${b.deposit_date ? ` · ${formatDateOnly(b.deposit_date)}` : ''}`
                            : t('deposits.blocked_payments_unknown_deposit')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border border-border p-2">
                  <p className="text-[11px] text-muted-foreground">
                    {t('deposits.blocked_payments_payout_total')}
                  </p>
                  <p className="text-sm font-semibold" data-testid="deposits-blocked-payout-total">
                    {formatCurrency(blockedModal.payoutTotal, locale)}
                  </p>
                </div>
                <div className="rounded-lg border border-border p-2">
                  <p className="text-[11px] text-muted-foreground">
                    {t('deposits.blocked_payments_excluded_total')}
                  </p>
                  <p className="text-sm font-semibold text-destructive"
                    data-testid="deposits-blocked-excluded-total">
                    {formatCurrency(blockedModal.blockedTotal, locale)}
                  </p>
                </div>
                <div className="rounded-lg border border-border p-2">
                  <p className="text-[11px] text-muted-foreground">
                    {t('deposits.blocked_payments_result_total')}
                  </p>
                  <p className="text-sm font-semibold" data-testid="deposits-blocked-result-total">
                    {formatCurrency(
                      (blockedModal.payoutTotal || 0) - (blockedModal.blockedTotal || 0),
                      locale)}
                  </p>
                </div>
              </div>
              <p className="text-xs text-destructive font-medium"
                data-testid="deposits-blocked-payments-warning">
                {t('deposits.blocked_payments_warning')}
              </p>
            </div>
          )}
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={excludeAndCommit}
              disabled={excludeBusy}
              data-testid="deposits-blocked-payments-exclude-btn">
              {excludeBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('deposits.blocked_payments_exclude_btn')}
            </Button>
            <Button variant="default" size="sm" onClick={() => setBlockedModal(null)}
              disabled={excludeBusy}
              data-testid="deposits-blocked-payments-close-btn">
              {t('deposits.blocked_payments_close_btn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Marcar payout completo como reconciliado ───────────────────── */}
      <Dialog open={!!groupMark} onOpenChange={(o) => !o && setGroupMark(null)}>
        <DialogContent data-testid="deposits-group-mark-dialog">
          <DialogHeader>
            <DialogTitle>{t('deposits.group_mark_title')}</DialogTitle>
            <DialogDescription>{t('deposits.group_mark_desc')}</DialogDescription>
          </DialogHeader>
          {groupMark && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground" data-testid="deposits-group-mark-count">
                {groupMark.group.pending_count} / {groupMark.group.statement_count} {t('deposits.view_statements')}
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="group-mark-note" className="text-xs">
                  {t('deposits.mark_reconciled_note')}
                </Label>
                <Input
                  id="group-mark-note"
                  value={groupMark.note || ''}
                  onChange={(e) => setGroupMark({ ...groupMark, note: e.target.value })}
                  className="h-8 text-xs"
                  data-testid="deposits-group-mark-note-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setGroupMark(null)}
              data-testid="deposits-group-mark-cancel-btn">
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={runGroupMark}
              disabled={groupBusy || !groupMark?.group?.pending_count}
              data-testid="deposits-group-mark-confirm-btn">
              {groupBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('deposits.group_mark_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PayoutGroupsTable({ groups, loading, expanded, toggleGroup, onCommit, onMark, onToggleReview, onGenerateAggregate, aggBusy, navigate, locale, t, setGAdj, removeGroupAdjustment }) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-0">
        <Table data-testid="deposits-groups-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>{t('common.date')}</TableHead>
              <TableHead>{t('deposits.col_payment_id')}</TableHead>
              <TableHead className="text-right">{t('deposits.col_statements')}</TableHead>
              <TableHead className="text-right">{t('deposits.col_total_bank')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-10">
                  {t('deposits.group_empty')}
                </TableCell>
              </TableRow>
            )}
            {groups.map((g) => {
              const isOpen = expanded.has(g.group_key);
              return (
                <React.Fragment key={g.group_key}>
                  <TableRow data-testid={`payout-group-row-${g.group_key}`}>
                    <TableCell className="w-10">
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.group_key)}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={t('deposits.group_expand')}
                        data-testid={`payout-group-toggle-${g.group_key}`}
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateOnly(g.last_statement_date || g.default_txn_date, locale)}
                      {g.bank_paid_date && (
                        <span className="block text-[11px] font-medium text-foreground"
                          data-testid={`payout-group-bank-paid-${g.group_key}`}>
                          {t('deposits.bank_paid_on')}{' '}
                          {formatDateOnly(g.bank_paid_date, locale)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {g.payment_id ? (
                        <span data-testid={`payout-group-payment-id-${g.group_key}`}>
                          {g.payment_id.slice(0, Math.max(0, g.payment_id.length - 7))}
                          <span className="font-bold text-foreground bg-[hsl(var(--warning-bg))] px-1 rounded">
                            {g.payment_id_last7}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">
                          {t('deposits.no_payment_id')}
                        </span>
                      )}
                      {g.source === 'statement' && (
                        <span className="block text-[10px] not-italic font-sans text-muted-foreground"
                          data-testid={`payout-group-source-${g.group_key}`}>
                          {t('deposits.group_source_statement')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {g.statement_count}
                      {g.pending_count > 0 && (
                        <span className="block text-[10px] font-normal text-muted-foreground"
                          data-testid={`payout-group-reviewed-${g.group_key}`}>
                          {t('deposits.reviewed_progress')
                            .replace('{{done}}', g.reviewed_count ?? 0)
                            .replace('{{total}}', g.statement_count)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-semibold"
                      data-testid={`payout-group-total-${g.group_key}`}>
                      {formatCurrency(g.total, locale)}
                      {g.sum_mismatch && (
                        <span className="block text-[10px] font-normal"
                          style={{ color: 'var(--warning-fg)' }}
                          data-testid={`payout-group-diff-${g.group_key}`}>
                          {t('deposits.group_sum_mismatch')
                            .replace('{{sum}}', formatCurrency(g.statements_sum, locale))
                            .replace('{{diff}}', formatCurrency(g.diff, locale))}
                        </span>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={g.status === 'mixed' ? 'pending' : g.status} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {(g.pending_count > 0 || g.status === 'deposit_deleted_in_qbo') && (
                        <>
                          <Button
                            size="sm"
                            className="mr-2"
                            disabled={!g.can_commit}
                            title={!g.can_commit ? t('deposits.group_blocked_title') : undefined}
                            onClick={() => onCommit(g)}
                            data-testid={`payout-group-commit-btn-${g.group_key}`}
                          >
                            {g.status === 'deposit_deleted_in_qbo'
                              ? t('deposits.group_recommit_btn')
                              : t('deposits.group_commit_btn')}
                          </Button>
                          {g.pending_count > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onMark(g)}
                              data-testid={`payout-group-mark-btn-${g.group_key}`}
                            >
                              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                              {t('deposits.group_mark_btn')}
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow data-testid={`payout-group-detail-${g.group_key}`}>
                      <TableCell colSpan={7} className="bg-muted/30">
                        {g.status === 'deposit_deleted_in_qbo' && (
                          <p className="text-[11px] mb-2 font-medium" style={{ color: 'var(--danger-fg)' }}
                            data-testid={`payout-group-deposit-deleted-${g.group_key}`}>
                            {t('deposits.deposit_deleted_message')}
                          </p>
                        )}
                        {!g.can_commit && g.blockers.length > 0 && g.pending_count > 0 && (
                          <p className="text-[11px] mb-2" style={{ color: 'var(--warning-fg)' }}
                            data-testid={`payout-group-blockers-${g.group_key}`}>
                            {t('deposits.group_blocked_hint')}{' '}
                            {g.blockers.map((b) => (b.payout_id
                              ? `${b.payout_id} (${t(`deposits.blocker_${b.reason}`)})`
                              : t(`deposits.blocker_${b.reason}`))).join(' · ')}
                          </p>
                        )}
                        {/* Fix 30 (2026-09-04): ajuste del payout completo — el
                            que cubre la diferencia contra el monto del banco. */}
                        {(g.group_adjustments || []).length > 0 && (
                          <div className="mb-2 space-y-1"
                            data-testid={`payout-group-adjustments-${g.group_key}`}>
                            {(g.group_adjustments || []).map((a) => (
                              <div key={a.id} className="flex items-center gap-2 text-[11px]">
                                <span className="tabular-nums font-semibold">
                                  {formatCurrency(a.amount, locale)}
                                </span>
                                <span className="text-muted-foreground">{a.account_name}</span>
                                {a.note && <span className="text-muted-foreground">· {a.note}</span>}
                                {g.pending_count > 0 && (
                                  <button type="button"
                                    className="underline text-muted-foreground hover:text-[hsl(var(--primary))]"
                                    onClick={() => removeGroupAdjustment(g.group_key, a.id)}
                                    data-testid={`payout-group-adj-remove-${a.id}`}>
                                    {t('common.delete')}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {g.sum_mismatch && g.pending_count > 0 && (
                          <Button size="sm" variant="outline" className="mb-2"
                            onClick={() => setGAdj({
                              group: g,
                              amount: String(g.missing_to_bank ?? ''),
                              account: '',
                              note: '',
                            })}
                            data-testid={`payout-group-add-adj-${g.group_key}`}>
                            {t('deposits.group_adj_add')}
                          </Button>
                        )}
                        <div className="space-y-1">
                          {g.statements.map((s) => (
                            <div key={s.payout_id}
                              className="flex items-center gap-3 text-xs py-1"
                              data-testid={`payout-group-statement-${s.payout_id}`}>
                              <span className="text-muted-foreground w-24 shrink-0">
                                {formatDateOnly(s.payout_date, locale)}
                              </span>
                              <button
                                type="button"
                                className="font-mono underline hover:text-[hsl(var(--primary))]"
                                onClick={() => navigate(`/depositos/${s.payout_id}`)}
                                data-testid={`payout-group-statement-link-${s.payout_id}`}
                              >
                                {s.payout_id}
                              </button>
                              <span className="tabular-nums font-medium">
                                {formatCurrency(s.net, locale)}
                              </span>
                              {s.balanced === true
                                ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success-fg))]" />
                                : <XCircle className="h-3.5 w-3.5 text-[hsl(var(--danger-fg))]" />}
                              {s.status === 'pending_review' && s.reviewed
                                ? <ReviewedBadge testid={`payout-group-statement-reviewed-${s.payout_id}`} />
                                : <StatusBadge status={s.status} />}
                              {s.status === 'pending_review' && (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground underline"
                                  onClick={() => onToggleReview(s.payout_id)}
                                  data-testid={`payout-group-statement-review-btn-${s.payout_id}`}
                                >
                                  <CheckCheck className="h-3.5 w-3.5" />
                                  {s.reviewed
                                    ? t('deposits.unmark_reviewed')
                                    : t('deposits.mark_reviewed')}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {(g.missing_statements || []).length > 0 && (
                          <div className="mt-3 pt-3 border-t border-border space-y-2"
                            data-testid={`payout-group-missing-${g.group_key}`}>
                            <p className="text-[11px] font-medium text-foreground">
                              {t('deposits.missing_statements_title')}
                            </p>
                            {(g.missing_statements || []).map((m) => (
                              <div key={m.statement_id}
                                className="flex items-center gap-3 text-xs py-1 flex-wrap"
                                data-testid={`payout-group-missing-statement-${m.statement_id}`}>
                                <span className="text-muted-foreground w-24 shrink-0">
                                  {m.statement_date ? formatDateOnly(m.statement_date, locale) : '—'}
                                </span>
                                <span className="font-mono">{m.statement_id}</span>
                                <span className="tabular-nums font-medium">
                                  {m.amount != null ? formatCurrency(m.amount, locale) : '—'}
                                </span>
                                <span className="text-[11px]" style={{ color: 'var(--warning-fg)' }}>
                                  {m.exists
                                    ? t('deposits.not_processed_yet')
                                    : t('deposits.statement_not_downloaded')}
                                </span>
                                {m.adjustment_only && (
                                  <span className="text-[11px] px-1.5 py-0.5 rounded border border-border text-muted-foreground"
                                    data-testid={`missing-statement-adjustment-badge-${m.statement_id}`}>
                                    {t('deposits.adjustment_only_badge')}
                                  </span>
                                )}
                                {m.reconcile_attempts > 0 && (
                                  <span className="text-[11px] text-muted-foreground">
                                    {t('deposits.statement_attempts')
                                      .replace('{{n}}', m.reconcile_attempts)}
                                  </span>
                                )}
                                {m.can_generate_aggregate && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={aggBusy === m.statement_id}
                                    title={t('deposits.generate_aggregate_hint')}
                                    onClick={() => onGenerateAggregate(m.statement_id)}
                                    data-testid={`generate-aggregate-btn-${m.statement_id}`}
                                  >
                                    {aggBusy === m.statement_id
                                      ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                      : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
                                    {t('deposits.generate_aggregate_btn')}
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function DepositReferenceMatches({ rows, navigate, locale, t }) {
  return (
    <Card className="bg-card border-[hsl(var(--primary))]/40 mb-4"
      data-testid="deposit-reference-matches-card">
      <CardContent className="p-4 space-y-2">
        <div>
          <p className="text-sm font-semibold" data-testid="deposit-reference-matches-title">
            {t('deposits.find_by_title')}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {t('deposits.find_by_hint')}
          </p>
        </div>
        {rows.map((r, i) => {
          const label = r.match_kind === 'invoice'
            ? t('deposits.find_by_invoice').replace('{{n}}', r.doc_number || r.invoice_id || '—')
            : t('deposits.find_by_order').replace('{{n}}', (r.order_ids || [])[0] || '—');
          return (
            <button
              key={`${r.payout_id}-${r.match_kind}-${r.invoice_id || (r.order_ids || [])[0] || i}`}
              type="button"
              onClick={() => navigate(`/depositos/${encodeURIComponent(r.payout_id)}`)}
              className="w-full flex items-center gap-3 text-xs py-1.5 flex-wrap border-t border-border pt-2 text-left hover:bg-muted/40 rounded"
              data-testid={`deposit-reference-match-${r.payout_id}-${r.match_kind}`}
            >
              <span className="text-muted-foreground w-24 shrink-0">
                {r.date ? formatDateOnly(r.date, locale) : '—'}
              </span>
              <span className="font-medium">{label}</span>
              {r.customer_name && (
                <span className="text-muted-foreground">{r.customer_name}</span>
              )}
              <span className="text-muted-foreground">→</span>
              <span className="font-mono">{r.payout_id}</span>
              {r.payment_id_last7 && (
                <span className="text-[11px] text-muted-foreground font-mono">
                  {t('deposits.find_by_bank_group').replace('{{n}}', r.payment_id_last7)}
                </span>
              )}
              <span className="tabular-nums font-medium">
                {r.amount != null ? formatCurrency(r.amount, locale) : '—'}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {t(`deposits.find_by_kind_${r.match_kind}`)}
              </span>
              <StatusBadge status={r.status} />
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}

function UnprocessedStatements({ rows, onGenerate, busyId, navigate, locale, t }) {
  return (
    <Card className="bg-card border-[hsl(var(--warning-border))] mb-4"
      data-testid="unprocessed-statements-card">
      <CardContent className="p-4 space-y-2">
        <div>
          <p className="text-sm font-semibold" data-testid="unprocessed-statements-title">
            {t('deposits.unprocessed_search_title')}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {t('deposits.unprocessed_search_hint')}
          </p>
        </div>
        {rows.map((s) => (
          <div key={s.statement_id}
            className="flex items-center gap-3 text-xs py-1 flex-wrap border-t border-border pt-2"
            data-testid={`unprocessed-statement-${s.statement_id}`}>
            <span className="text-muted-foreground w-24 shrink-0">
              {s.statement_date ? formatDateOnly(s.statement_date, locale) : '—'}
            </span>
            <span className="font-mono">{s.statement_id}</span>
            <span className="tabular-nums font-medium">
              {s.amount != null ? formatCurrency(s.amount, locale) : '—'}
            </span>
            <span className="text-[11px]" style={{ color: 'var(--warning-fg)' }}>
              {t('deposits.not_processed_yet')}
            </span>
            {s.adjustment_only && (
              <span className="text-[11px] px-1.5 py-0.5 rounded border border-border text-muted-foreground"
                data-testid={`unprocessed-statement-adjustment-badge-${s.statement_id}`}>
                {t('deposits.adjustment_only_badge')}
              </span>
            )}
            {s.reconcile_attempts > 0 && (
              <span className="text-[11px] text-muted-foreground">
                {t('deposits.statement_attempts').replace('{{n}}', s.reconcile_attempts)}
              </span>
            )}
            {s.payment_id && (
              <button
                type="button"
                className="font-mono text-[11px] underline hover:text-[hsl(var(--primary))]"
                onClick={() => navigate(`/depositos?payment=${s.payment_id}`)}
                data-testid={`unprocessed-statement-payment-${s.statement_id}`}
              >
                {s.payment_id}
              </button>
            )}
            {s.can_generate_aggregate && (
              <Button
                size="sm"
                variant="outline"
                disabled={busyId === s.statement_id}
                title={t('deposits.generate_aggregate_hint')}
                onClick={() => onGenerate(s.statement_id)}
                data-testid={`unprocessed-generate-aggregate-btn-${s.statement_id}`}
              >
                {busyId === s.statement_id
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
                {t('deposits.generate_aggregate_btn')}
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function SortableHead({ label, col, sortBy, sortDir, onSort, align, testid }) {
  const active = sortBy === col;
  return (
    <TableHead
      className={`cursor-pointer select-none hover:text-foreground ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onSort(col)}
      data-testid={testid}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
        {label}
        {active && (sortDir === 'asc'
          ? <ArrowUp className="h-3 w-3" />
          : <ArrowDown className="h-3 w-3" />)}
      </span>
    </TableHead>
  );
}

function PayoutTable({ rows, locale, t, navigate, emptyKey, selected, toggleRow, toggleAllRows, onToggleReview, sortBy, sortDir, onSort }) {
  const allChecked = rows.length > 0 && rows.every(r => selected.has(r.payout_id));
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-0">
        <Table data-testid="deposits-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allChecked}
                  onCheckedChange={(c) => toggleAllRows(rows, c === true)}
                  aria-label="select all"
                  data-testid="deposits-select-all"
                />
              </TableHead>
              <SortableHead label={t('common.date')} col="date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testid="deposits-sort-date" />
              <TableHead>Payout ID</TableHead>
              <TableHead className="text-right">{t('deposits.col_invoices')}</TableHead>
              <SortableHead label="Net" col="net" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" testid="deposits-sort-net" />
              <TableHead className="text-center">{t('deposits.col_balanced')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-xs text-muted-foreground py-10">
                  {t(emptyKey)}
                </TableCell>
              </TableRow>
            )}
            {rows.map((d) => (
              <TableRow key={d.payout_id} data-testid={`deposit-row-${d.payout_id}`}>
                <TableCell className="w-10">
                  <Checkbox
                    checked={selected.has(d.payout_id)}
                    onCheckedChange={() => toggleRow(d.payout_id)}
                    aria-label={`select ${d.payout_id}`}
                    data-testid={`deposit-select-${d.payout_id}`}
                  />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDateOnly(d.payout_date, locale)}
                </TableCell>
                <TableCell className="font-mono text-xs">{shortId(d.payout_id, 12, 4)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{d.invoice_count ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums text-sm font-medium">
                  {formatCurrency(d.net, locale)}
                </TableCell>
                <TableCell className="text-center">
                  {d.balanced === true && (
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success-fg))] mx-auto" data-testid={`deposit-balanced-${d.payout_id}`} />
                  )}
                  {d.balanced === false && (
                    <XCircle className="h-4 w-4 text-[hsl(var(--danger-fg))] mx-auto" data-testid={`deposit-unbalanced-${d.payout_id}`} />
                  )}
                  {d.balanced == null && <span className="text-muted-foreground text-xs">—</span>}
                </TableCell>
                <TableCell>
                  {d.status === 'pending_review' && d.reviewed
                    ? <ReviewedBadge testid={`deposit-reviewed-badge-${d.payout_id}`} />
                    : <StatusBadge status={d.status} />}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1.5">
                    {d.status === 'pending_review' && onToggleReview && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        title={d.reviewed ? t('deposits.unmark_reviewed') : t('deposits.mark_reviewed')}
                        onClick={() => onToggleReview(d.payout_id)}
                        data-testid={`deposit-toggle-review-btn-${d.payout_id}`}
                      >
                        <CheckCheck className={`h-3.5 w-3.5 mr-1.5 ${d.reviewed ? 'text-[hsl(var(--success-fg))]' : ''}`} />
                        {d.reviewed ? t('deposits.unmark_reviewed') : t('deposits.mark_reviewed')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/depositos/${d.payout_id}`)}
                      data-testid={`deposit-review-btn-${d.payout_id}`}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      {t('deposits.btn_review')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
